import { motion } from "framer-motion";
import { Check, X, ArrowUpRight } from "lucide-react";
import { GridPattern, FloatingOrb, CornerAccent } from "@/components/ui/decorative-elements";

interface ForWhoSectionProps {
  language: 'es' | 'en';
}

export const ForWhoSection = ({ language }: ForWhoSectionProps) => {
  const content = {
    es: {
      overline: "Claridad desde el inicio",
      title: "¿Somos el",
      titleAccent: "match correcto?",
      idealTitle: "Sí, si eres...",
      idealItems: [
        { text: "Fabricante B2B que quiere más visibilidad online", highlight: "Fabricante B2B" },
        { text: "Proveedor de servicios profesionales", highlight: "servicios profesionales" },
        { text: "Empresa que invierte en crecimiento real y sostenible", highlight: "crecimiento real" },
        { text: "Negocio listo para escalar con estrategia digital", highlight: "escalar" },
        { text: "Equipo que valora resultados medibles sobre promesas", highlight: "resultados medibles" }
      ],
      notForTitle: "No, si buscas...",
      notForItems: [
        "El precio más bajo del mercado",
        "Resultados de la noche a la mañana",
        "Proyectos sin visión a largo plazo",
        "Promesas en lugar de estrategia"
      ]
    },
    en: {
      overline: "Clarity from the start",
      title: "Are we the",
      titleAccent: "right match?",
      idealTitle: "Yes, if you are...",
      idealItems: [
        { text: "B2B manufacturer seeking more online visibility", highlight: "B2B manufacturer" },
        { text: "Professional service provider", highlight: "Professional service" },
        { text: "Company investing in real, sustainable growth", highlight: "real growth" },
        { text: "Business ready to scale with digital strategy", highlight: "scale" },
        { text: "Team that values measurable results over promises", highlight: "measurable results" }
      ],
      notForTitle: "No, if you're looking for...",
      notForItems: [
        "The lowest price on the market",
        "Overnight results",
        "Projects without long-term vision",
        "Promises instead of strategy"
      ]
    }
  };

  const t = content[language];

  return (
    <section className="relative py-24 lg:py-32 overflow-hidden bg-slate-100">
      {/* Background Pattern - very subtle */}
      <div className="absolute inset-0 opacity-30">
        <GridPattern className="text-slate-400/[0.04]" />
      </div>
      
      {/* Floating Accents - more subtle */}
      <FloatingOrb className="top-20 right-20" color="gold" size="md" delay={0} />
      <FloatingOrb className="bottom-40 left-10" color="accent" size="sm" delay={2} />
      
      <CornerAccent position="top-left" className="m-12 text-slate-400/20" />

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.span 
            className="inline-block text-gold text-sm font-semibold uppercase tracking-[0.2em] mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            {t.overline}
          </motion.span>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900">
            {t.title}{" "}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-slate-700 via-gold to-amber-500 bg-clip-text text-transparent">{t.titleAccent}</span>
              <motion.svg
                className="absolute -bottom-2 left-0 w-full"
                viewBox="0 0 200 12"
                fill="none"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.8 }}
              >
                <motion.path
                  d="M2 8C50 2 150 2 198 8"
                  stroke="hsl(var(--gold))"
                  strokeWidth="3"
                  strokeLinecap="round"
                  fill="none"
                />
              </motion.svg>
            </span>
          </h2>
        </motion.div>

        {/* Asymmetric Bento Layout */}
        <div className="grid grid-cols-12 gap-4 lg:gap-5 max-w-6xl mx-auto">
          {/* YES Column - Large Card with Image Background */}
          <motion.div 
            className="col-span-12 lg:col-span-7 relative rounded-2xl overflow-hidden min-h-[500px] ring-1 ring-slate-300"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Background Image - more visible */}
            <div className="absolute inset-0">
              <img 
                src="/media/luis-roc/luisroc-pbp1.png" 
                alt="Luis ROC working with client"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-800/85 to-slate-700/80" />
            </div>
            
            {/* Content */}
            <div className="relative z-10 p-8 lg:p-10 h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 rounded-xl bg-emerald-500/20 backdrop-blur-sm border border-emerald-500/30 flex items-center justify-center">
                  <Check className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold text-white">{t.idealTitle}</h3>
              </div>
              
              {/* Items */}
              <div className="space-y-3 flex-1">
                {t.idealItems.map((item, index) => (
                  <motion.div 
                    key={index}
                    className="group flex items-start gap-4 p-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 hover:bg-white/15 hover:border-white/20 transition-all duration-300 cursor-default"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-emerald-500/30 transition-colors">
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                    </div>
                    <span className="text-white/90 text-lg leading-relaxed">
                      {item.text.split(item.highlight).map((part, i) => (
                        <span key={i}>
                          {part}
                          {i === 0 && <span className="text-emerald-400 font-semibold">{item.highlight}</span>}
                        </span>
                      ))}
                    </span>
                    <ArrowUpRight className="w-5 h-5 text-white/30 group-hover:text-emerald-400 transition-colors ml-auto flex-shrink-0" />
                  </motion.div>
                ))}
              </div>

              {/* Decorative corner */}
              <div className="absolute top-0 right-0 w-24 h-24 border-r-2 border-t-2 border-emerald-500/30 rounded-tr-2xl" />
            </div>
          </motion.div>

          {/* NO Column - Stack of Cards */}
          <motion.div 
            className="col-span-12 lg:col-span-5 flex flex-col gap-3"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Header Card */}
            <div className="bg-slate-800 rounded-xl p-6 lg:p-8">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 rounded-lg bg-slate-700 flex items-center justify-center">
                  <X className="w-6 h-6 text-slate-400" />
                </div>
                <h3 className="text-xl lg:text-2xl font-bold text-white">{t.notForTitle}</h3>
              </div>
            </div>

            {/* Items - better contrast */}
            {t.notForItems.map((item, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-300 group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 + index * 0.1 }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 group-hover:bg-slate-200 transition-colors">
                    <X className="w-4 h-4 text-slate-500" />
                  </div>
                  <span className="text-slate-600 group-hover:text-slate-800 transition-colors">
                    {item}
                  </span>
                </div>
              </motion.div>
            ))}

            {/* Visual accent card - lighter overlay */}
            <motion.div 
              className="relative flex-1 min-h-[120px] rounded-xl overflow-hidden ring-1 ring-slate-300"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 }}
            >
              <img 
                src="/media/luis-roc/luisroc-expo.png" 
                alt="Luis ROC at expo"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent flex items-end p-4">
                <p className="text-white text-sm font-medium">
                  {language === 'es' ? 'Trabajo con empresas serias' : 'I work with serious companies'}
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
